import { z } from "zod";

export const ApprovalConfigSchema = z.object({
  id: z.string().uuid(),
  // Workspace ids are slug strings (e.g. "acme-corp-workspace"), not UUIDs —
  // a .uuid() here made every session page toast "Failed to fetch approval
  // configs" in any workspace with a config row.
  workspaceId: z.string(),
  entityType: z.string(),
  changeType: z.string(),
  isEnabled: z.boolean(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ApprovalConfig = z.infer<typeof ApprovalConfigSchema>;

export const ApprovalConfigStepSchema = z.object({
  id: z.string().uuid(),
  configId: z.string().uuid(),
  stepOrder: z.number(),
  approverRoles: z.array(z.string()),
  requiredCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ApprovalConfigStep = z.infer<typeof ApprovalConfigStepSchema>;

export const ApprovalConfigWithStepsSchema = ApprovalConfigSchema.extend({
  steps: z.array(ApprovalConfigStepSchema),
});

export type ApprovalConfigWithSteps = z.infer<
  typeof ApprovalConfigWithStepsSchema
>;

export const ApprovalActionSchema = z.object({
  id: z.string().uuid(),
  requestId: z.string().uuid(),
  actorId: z.string().uuid().nullable(),
  action: z.string(),
  stepId: z.string().uuid().nullable().optional(),
  comment: z.string().nullable().optional(),
  actionMetadata: z.record(z.unknown()).nullable().optional(),
  createdAt: z.string(),
});

export type ApprovalAction = z.infer<typeof ApprovalActionSchema>;

export const ApprovalRequesterSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  createdAt: z.string(),
});

export type ApprovalRequester = z.infer<typeof ApprovalRequesterSchema>;

export const ApprovalRequestSchema = z.object({
  id: z.string().uuid(),
  configId: z.string().uuid().nullable(),
  entityId: z.string().uuid(),
  entityType: z.string(),
  changeType: z.string(),
  requesterId: z.string().uuid().nullable(),
  status: z.string(),
  description: z.string().nullable().optional(),
  currentStepOrder: z.number().nullable().optional(),
  requestMetadata: z.record(z.unknown()).nullable().optional(),
  currentSnapshot: z.record(z.unknown()).nullable().optional(),
  proposedSnapshot: z.record(z.unknown()).nullable().optional(),
  changeDiff: z.record(z.unknown()).nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  configSteps: z.array(ApprovalConfigStepSchema).optional(),
  actions: z.array(ApprovalActionSchema).optional(),
  requester: ApprovalRequesterSchema.nullable().optional(),
  // Legacy flat fields — may still appear on detail endpoint
  requesterEmail: z.string().nullable().optional(),
  entityName: z.string().nullable().optional(),
});

export type ApprovalRequest = z.infer<typeof ApprovalRequestSchema>;
